import { useNavigate } from "react-router-dom";
import { FiFile, FiEdit2, FiTrash2, FiChevronRight, FiCalendar, FiPackage } from "react-icons/fi";
import { getWarrantyStatus } from "../SubscriptionsUtils/warrantyStatus";
import { exportToExcel } from "../SubscriptionsUtils/exportUtils";

export default function AssetTable({ data, allData, onEdit, onDelete }) {
  const navigate = useNavigate();

  const columns = [
    { header: "Asset Name", key: "name" },
    { header: "Type", key: "type" },
    { header: "Assigned To", key: "assignedTo" },
    { header: "Warranty End", key: "warrantyEnd" },
    {
      header: "Warranty Status",
      accessor: (row) => {
        const warranty = getWarrantyStatus(row.warrantyEnd);
        return `${warranty.label}${warranty.daysLeft !== undefined ? ` (${warranty.daysLeft}d)` : ""}`;
      },
    },
    { header: "Status", key: "status" },
  ];

  const handleExportExcel = () => {
    const exportData = allData || data;
    exportToExcel(exportData, columns, "assets", "Assets");
  };

  // Format date nicely
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const getStatusBadge = (warranty) => {
    const styles = {
      red: "bg-red-50 text-red-700 border border-red-200",
      orange: "bg-orange-50 text-orange-700 border border-orange-200",
      yellow: "bg-amber-50 text-amber-700 border border-amber-200",
      green: "bg-emerald-50 text-emerald-700 border border-emerald-200",
      gray: "bg-gray-50 text-gray-600 border border-gray-200"
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold ${styles[warranty.color] || styles.gray}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${
          warranty.color === "red" ? "bg-red-500" :
          warranty.color === "orange" ? "bg-orange-500" :
          warranty.color === "yellow" ? "bg-amber-500" :
          warranty.color === "green" ? "bg-emerald-500" :
          "bg-gray-400"
        }`}></span>
        {warranty.label}
        {warranty.daysLeft !== undefined && (
          <span className="opacity-75">({warranty.daysLeft}d)</span>
        )}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    if (!type) return <span className="text-gray-400">—</span>;
    
    const colors = {
      "Laptop": "bg-blue-50 text-blue-700 border-blue-200",
      "Desktop": "bg-indigo-50 text-indigo-700 border-indigo-200",
      "Monitor": "bg-purple-50 text-purple-700 border-purple-200",
      "Phone": "bg-pink-50 text-pink-700 border-pink-200",
      "Tablet": "bg-cyan-50 text-cyan-700 border-cyan-200",
      "Server": "bg-slate-50 text-slate-700 border-slate-200",
      "Printer": "bg-orange-50 text-orange-700 border-orange-200",
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border ${colors[type] || "bg-gray-50 text-gray-600 border-gray-200"}`}>
        {type}
      </span>
    );
  };

  // Get initials or icon color based on asset type
  const getAssetColor = (type) => {
    const colors = {
      "Laptop": "from-blue-500 to-blue-600",
      "Desktop": "from-indigo-500 to-indigo-600",
      "Monitor": "from-purple-500 to-purple-600",
      "Phone": "from-pink-500 to-pink-600",
      "Tablet": "from-cyan-500 to-cyan-600",
      "Server": "from-slate-500 to-slate-600",
      "Printer": "from-orange-500 to-orange-600",
    };
    return colors[type] || "from-gray-500 to-gray-600";
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm">
      {/* Export Buttons */}
      {data.length > 0 && (
        <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-3 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <p className="text-sm text-gray-500">
            <span className="font-semibold text-gray-900">{data.length}</span> asset{data.length !== 1 ? 's' : ''}
          </p>
          <button
            onClick={handleExportExcel}
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
          >
            <FiFile className="text-green-600" />
            Export Excel
          </button>
        </div>
      )}

      {/* Mobile Card View - visible on small screens */}
      <div className="block md:hidden divide-y divide-gray-100 max-h-[60vh] overflow-y-auto">
        {data.map((asset) => {
          const warranty = getWarrantyStatus(asset.warrantyEnd);

          return (
            <div
              key={asset._id}
              onClick={() => navigate(`/assets/${asset._id}`)}
              className="p-4 hover:bg-gray-50 cursor-pointer active:bg-gray-100 transition-colors"
            >
              {/* Header row with name and status */}
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate text-sm">
                    {asset.name}
                  </h3>
                  <p className="text-xs text-gray-500 truncate mt-0.5">
                    {asset.type || "No type"}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  {getStatusBadge(warranty)}
                </div>
              </div>

              {/* Info grid */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs mb-3">
                <div>
                  <span className="text-gray-500">Assigned:</span>
                  <span className="ml-1 text-gray-900">{asset.assignedTo || "—"}</span>
                </div>
                <div>
                  <span className="text-gray-500">Warranty:</span>
                  <span className="ml-1 text-gray-900">{formatDisplayDate(asset.warrantyEnd)}</span>
                </div>
              </div>

              {/* Actions row */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <div className="flex items-center gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(asset);
                    }}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium py-1 px-2 rounded-md hover:bg-blue-50 transition-colors"
                  >
                    <FiEdit2 className="w-3.5 h-3.5" />
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(asset._id);
                    }}
                    className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800 font-medium py-1 px-2 rounded-md hover:bg-red-50 transition-colors"
                  >
                    <FiTrash2 className="w-3.5 h-3.5" />
                    Delete
                  </button>
                </div>
                <FiChevronRight className="w-4 h-4 text-gray-400" />
              </div>
            </div>
          );
        })}

        {data.length === 0 && (
          <div className="p-8 text-center text-gray-500 text-sm">
            No assets found
          </div>
        )}
      </div>

      {/* Desktop Table View - hidden on small screens */}
      <div className="hidden md:block max-h-[60vh] overflow-y-auto">
        <table className="w-full">
          <thead className="sticky top-0 z-10">
            <tr className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200">
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                Asset
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                Assigned To
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                Warranty End
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {data.map((asset, index) => {
              const warranty = getWarrantyStatus(asset.warrantyEnd);

              return (
                <tr
                  key={asset._id}
                  onClick={() => navigate(`/assets/${asset._id}`)}
                  className={`group cursor-pointer transition-all duration-150 ${
                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                  } hover:bg-purple-50/50`}
                >
                  {/* Asset Name */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${getAssetColor(asset.type)} flex items-center justify-center text-white shadow-sm`}>
                        <FiPackage className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 truncate max-w-[200px] group-hover:text-purple-600 transition-colors">
                          {asset.name}
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* Type */}
                  <td className="px-6 py-4">
                    {getTypeBadge(asset.type)}
                  </td>

                  {/* Assigned To */}
                  <td className="px-6 py-4">
                    {asset.assignedTo ? (
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center text-gray-600 text-xs font-semibold">
                          {asset.assignedTo.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-sm text-gray-700 font-medium truncate max-w-[120px]">
                          {asset.assignedTo}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">Unassigned</span>
                    )}
                  </td>

                  {/* Warranty End Date */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-gray-700">
                      <FiCalendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium">
                        {formatDisplayDate(asset.warrantyEnd)}
                      </span>
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-6 py-4">
                    {getStatusBadge(warranty)}
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(asset);
                        }}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                        title="Edit"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(asset._id);
                        }}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        title="Delete"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                      <FiChevronRight className="w-4 h-4 text-gray-300 group-hover:text-purple-400 group-hover:translate-x-0.5 transition-all ml-1" />
                    </div>
                  </td>
                </tr>
              );
            })}

            {data.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <div className="text-gray-400">
                    <FiPackage className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm font-medium">No assets found</p>
                    <p className="text-xs mt-1">Try adjusting your filters</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
